import { createAccountGeneralInfoSchema } from '@/lib/accountFormValidators';
import { extractErrorMessage } from '@/lib/errorUtils';
import { accountFormService } from '@/services/accountFormService';
import { AccountFormData } from '@/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

/**
 * Custom hook for Account General Info form state management
 * Following the same patterns as other form hooks in the project
 */
export const useAccountGeneralInfoForm = (
  token: string,
  tForms: (key: string) => string,
  initialData?: Partial<AccountFormData>
) => {
  const queryClient = useQueryClient();

  // Create form with translated validation schema
  const form = useForm<AccountFormData>({
    resolver: zodResolver(createAccountGeneralInfoSchema(tForms)),
    mode: 'onTouched', // Validate when field is touched/blurred
    reValidateMode: 'onChange', // Re-validate on change after first validation
    defaultValues: {
      companyType: '',
      language: '',
      name: '',
      nameAddition: '',
      country: '',
      street: '',
      streetNo: '',
      poBox: '',
      postalCode: '',
      city: '',
      canton: '',
      website: '',
      industry: '',
      commercialRegisterNumber: '',
      ...initialData,
    },
  });

  // Form submission mutation
  const submitMutation = useMutation({
    mutationFn: (data: AccountFormData) =>
      accountFormService.submitAccountForm(token, data),
    onSuccess: response => {
      toast.success('Success', {
        description:
          response.message || 'Account information updated successfully',
      });

      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['accountData', token] });
    },
    onError: (error: Error) => {
      const errorMessage = extractErrorMessage(
        error,
        'Failed to update account information'
      );
      toast.error('Error', {
        description: errorMessage,
      });
    },
  });

  // Get form options
  const { data: formOptions, isLoading: isLoadingOptions } = useQuery({
    queryKey: ['formOptions'],
    queryFn: () => accountFormService.getFormOptions(),
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour
  });

  // Watch country and postal code for dependent field updates
  const watchedCountry = form.watch('country');
  const watchedPostalCode = form.watch('postalCode');

  // Get postal codes based on selected country
  const { data: postalCodes, isLoading: isLoadingPostalCodes } = useQuery({
    queryKey: ['postalCodes', watchedCountry],
    queryFn: () => accountFormService.getPostalCodes(watchedCountry),
    enabled: !!watchedCountry,
    staleTime: 60 * 60 * 1000, // 1 hour
  });

  // Get cities based on selected country and postal code
  const { data: cities, isLoading: isLoadingCities } = useQuery({
    queryKey: ['cities', watchedCountry, watchedPostalCode],
    queryFn: () =>
      accountFormService.getCities(watchedCountry, watchedPostalCode),
    enabled: !!watchedCountry && !!watchedPostalCode,
    staleTime: 60 * 60 * 1000, // 1 hour
  });

  // Form submission handler
  const handleSubmit = async (data: AccountFormData) => {
    await submitMutation.mutateAsync(data);
  };

  // Field change handlers for dependent fields
  const handleCountryChange = (country: string) => {
    form.setValue('country', country);
    form.setValue('postalCode', '');
    form.setValue('city', '');
    form.setValue('canton', '');
  };

  const handlePostalCodeChange = (postalCode: string) => {
    form.setValue('postalCode', postalCode);
    form.setValue('city', '');
  };

  return {
    form,
    onSubmit: handleSubmit,
    isSubmitting: submitMutation.isPending,
    submitError: submitMutation.error,
    formOptions: {
      companyTypes: formOptions?.companyTypes || [],
      languages: formOptions?.languages || [],
      countries: formOptions?.countries || [],
      industries: formOptions?.industries || [],
      cantons: formOptions?.cantons || [],
      postalCodes: postalCodes || [],
      cities: cities || [],
    },
    isLoading: {
      options: isLoadingOptions,
      postalCodes: isLoadingPostalCodes,
      cities: isLoadingCities,
    },
    handlers: {
      handleCountryChange,
      handlePostalCodeChange,
    },
  };
};
